import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Save, FileText, Palette, Type, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
interface DeliveryNoteSettings {
  companyName: string;
  companySubtitle: string;
  showLogo: boolean;
  showOrderDateTime: boolean;
  showDeliveryDateTime: boolean;
  showDeliveryType: boolean;
  showEmployeeCode: boolean;
  showRequests: boolean;
  showQuantity: boolean;
  showSignature: boolean;
  showNotes: boolean;
  notesLabel: string;
  showFooter: boolean;
  footerText: string;
  // Aesthetic settings
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  fontSize: string;
  headerStyle: 'modern' | 'classic' | 'minimal' | 'bold';
  tableStyle: 'striped' | 'bordered' | 'minimal' | 'compact';
  borderRadius: string;
  logoUrl: string;
  logoSize: string;
}
export function DeliveryNoteSettings() {
  const {
    showToast
  } = useToast();
  const [settings, setSettings] = useState<DeliveryNoteSettings>(() => {
    const saved = localStorage.getItem('delivery_note_settings');
    return saved ? JSON.parse(saved) : {
      companyName: 'La Cochera',
      companySubtitle: 'Restaurant',
      showLogo: true,
      showOrderDateTime: false,
      showDeliveryDateTime: false,
      showDeliveryType: false,
      showEmployeeCode: true,
      showRequests: false,
      showQuantity: true,
      showSignature: true,
      showNotes: true,
      notesLabel: 'Notas',
      showFooter: true,
      footerText: 'Documento generado por La Cochera Restaurant',
      // Aesthetic defaults
      primaryColor: '#000000',
      accentColor: '#3b82f6',
      fontFamily: 'Segoe UI',
      fontSize: '11pt',
      headerStyle: 'modern',
      tableStyle: 'striped',
      borderRadius: '8px',
      logoUrl: '',
      logoSize: '60px'
    };
  });
  const handleSave = () => {
    localStorage.setItem('delivery_note_settings', JSON.stringify(settings));
    showToast('success', 'Configuración guardada', 'Los cambios se aplicarán en el próximo conduce');
  };
  const fontOptions = [{
    value: 'Segoe UI',
    label: 'Segoe UI (Moderno)'
  }, {
    value: 'Arial',
    label: 'Arial (Clásico)'
  }, {
    value: 'Times New Roman',
    label: 'Times New Roman (Formal)'
  }, {
    value: 'Georgia',
    label: 'Georgia (Elegante)'
  }, {
    value: 'Courier New',
    label: 'Courier New (Monoespaciado)'
  }, {
    value: 'Verdana',
    label: 'Verdana (Legible)'
  }];
  const fontSizeOptions = [{
    value: '9pt',
    label: 'Pequeño (9pt)'
  }, {
    value: '10pt',
    label: 'Normal (10pt)'
  }, {
    value: '11pt',
    label: 'Mediano (11pt)'
  }, {
    value: '12pt',
    label: 'Grande (12pt)'
  }];
  const headerStyleOptions = [{
    value: 'modern',
    label: 'Moderno',
    description: 'Limpio y minimalista'
  }, {
    value: 'classic',
    label: 'Clásico',
    description: 'Tradicional y formal'
  }, {
    value: 'minimal',
    label: 'Minimal',
    description: 'Ultra simple'
  }, {
    value: 'bold',
    label: 'Bold',
    description: 'Impactante y destacado'
  }];
  const tableStyleOptions = [{
    value: 'striped',
    label: 'Rayado',
    description: 'Filas alternadas'
  }, {
    value: 'bordered',
    label: 'Con Bordes',
    description: 'Bordes completos'
  }, {
    value: 'minimal',
    label: 'Minimal',
    description: 'Solo líneas esenciales'
  }, {
    value: 'compact',
    label: 'Compacto',
    description: 'Espaciado reducido'
  }];
  return <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-id="element-984">
      <div className="mb-8" data-id="element-985">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-986">
          Configuración del Conduce
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-987">
          Personaliza el formato, contenido y estética del conduce de entrega
        </p>
      </div>

      <div className="space-y-6" data-id="element-988">
        {/* Company Information */}
        <Card data-id="element-989">
          <div className="flex items-center gap-3 mb-6" data-id="element-990">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg" data-id="element-991">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-992" />
            </div>
            <div data-id="element-993">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-994">
                Información de la Empresa
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-995">
                Datos que aparecen en el encabezado
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-996">
            <div data-id="element-997">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-998">
                Nombre de la Empresa
              </label>
              <input type="text" value={settings.companyName} onChange={e => setSettings({
              ...settings,
              companyName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="La Cochera" data-id="element-999" />
            </div>

            <div data-id="element-1000">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1001">
                Subtítulo
              </label>
              <input type="text" value={settings.companySubtitle} onChange={e => setSettings({
              ...settings,
              companySubtitle: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Restaurant" data-id="element-1002" />
            </div>

            <div data-id="element-1003">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1004">
                Logo (URL de imagen)
              </label>
              <input type="text" value={settings.logoUrl} onChange={e => setSettings({
              ...settings,
              logoUrl: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="https://ejemplo.com/logo.png" data-id="element-1005" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1006">
                Deja vacío para mostrar solo texto
              </p>
            </div>

            {settings.logoUrl && <div data-id="element-1007">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1008">
                  Tamaño del Logo
                </label>
                <select value={settings.logoSize} onChange={e => setSettings({
              ...settings,
              logoSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1009">
                  <option value="40px" data-id="element-1010">Pequeño (40px)</option>
                  <option value="50px" data-id="element-1011">Mediano (50px)</option>
                  <option value="60px" data-id="element-1012">Grande (60px)</option>
                  <option value="80px" data-id="element-1013">Extra Grande (80px)</option>
                </select>
              </div>}
          </div>
        </Card>

        {/* Aesthetic Settings - Colors */}
        <Card data-id="element-1014">
          <div className="flex items-center gap-3 mb-6" data-id="element-1015">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg" data-id="element-1016">
              <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1017" />
            </div>
            <div data-id="element-1018">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1019">
                Colores y Estilo Visual
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1020">
                Personaliza la paleta de colores del documento
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1021">
            <div data-id="element-1022">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1023">
                Color Principal
              </label>
              <div className="flex gap-3 items-center" data-id="element-1024">
                <input type="color" value={settings.primaryColor} onChange={e => setSettings({
                ...settings,
                primaryColor: e.target.value
              })} className="w-16 h-10 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer" data-id="element-1025" />
                <input type="text" value={settings.primaryColor} onChange={e => setSettings({
                ...settings,
                primaryColor: e.target.value
              })} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm" placeholder="#000000" data-id="element-1026" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1027">
                Usado en encabezados y bordes principales
              </p>
            </div>

            <div data-id="element-1028">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1029">
                Color de Acento
              </label>
              <div className="flex gap-3 items-center" data-id="element-1030">
                <input type="color" value={settings.accentColor} onChange={e => setSettings({
                ...settings,
                accentColor: e.target.value
              })} className="w-16 h-10 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer" data-id="element-1031" />
                <input type="text" value={settings.accentColor} onChange={e => setSettings({
                ...settings,
                accentColor: e.target.value
              })} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm" placeholder="#3b82f6" data-id="element-1032" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1033">
                Usado en detalles y elementos destacados
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1034">
            <div data-id="element-1035">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1036">
                Estilo del Encabezado
              </label>
              <div className="space-y-2" data-id="element-1037">
                {headerStyleOptions.map(option => <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1038">
                    <input type="radio" name="headerStyle" value={option.value} checked={settings.headerStyle === option.value} onChange={e => setSettings({
                  ...settings,
                  headerStyle: e.target.value as any
                })} className="w-4 h-4 text-black focus:ring-black dark:text-white dark:focus:ring-white" data-id="element-1039" />
                    <div data-id="element-1040">
                      <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1041">
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1042">
                        {option.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </div>

            <div data-id="element-1043">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1044">
                Estilo de la Tabla
              </label>
              <div className="space-y-2" data-id="element-1045">
                {tableStyleOptions.map(option => <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1046">
                    <input type="radio" name="tableStyle" value={option.value} checked={settings.tableStyle === option.value} onChange={e => setSettings({
                  ...settings,
                  tableStyle: e.target.value as any
                })} className="w-4 h-4 text-black focus:ring-black dark:text-white dark:focus:ring-white" data-id="element-1047" />
                    <div data-id="element-1048">
                      <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1049">
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1050">
                        {option.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </div>
          </div>

          <div className="mt-6" data-id="element-1051">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1052">
              Redondeo de Bordes
            </label>
            <select value={settings.borderRadius} onChange={e => setSettings({
            ...settings,
            borderRadius: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1053">
              <option value="0px" data-id="element-1054">Sin redondeo (Cuadrado)</option>
              <option value="4px" data-id="element-1055">Ligero (4px)</option>
              <option value="8px" data-id="element-1056">Moderado (8px)</option>
              <option value="12px" data-id="element-1057">Redondeado (12px)</option>
            </select>
          </div>
        </Card>

        {/* Typography Settings */}
        <Card data-id="element-1058">
          <div className="flex items-center gap-3 mb-6" data-id="element-1059">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg" data-id="element-1060">
              <Type className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1061" />
            </div>
            <div data-id="element-1062">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1063">
                Tipografía
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1064">
                Personaliza la fuente y tamaño del texto
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1065">
            <div data-id="element-1066">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1067">
                Familia de Fuente
              </label>
              <select value={settings.fontFamily} onChange={e => setSettings({
              ...settings,
              fontFamily: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1068">
                {fontOptions.map(option => <option key={option.value} value={option.value} data-id="element-1069">
                    {option.label}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1070">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1071">
                Tamaño de Fuente
              </label>
              <select value={settings.fontSize} onChange={e => setSettings({
              ...settings,
              fontSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1072">
                {fontSizeOptions.map(option => <option key={option.value} value={option.value} data-id="element-1073">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>
        </Card>

        {/* Table Columns */}
        <Card data-id="element-1074">
          <div className="flex items-center gap-3 mb-6" data-id="element-1075">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg" data-id="element-1076">
              <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-1077" />
            </div>
            <div data-id="element-1078">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1079">
                Columnas de la Tabla
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1080">
                Selecciona qué columnas mostrar
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1081">
            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1082">
              <div data-id="element-1083">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1084">
                  Fecha/Hora Orden
                </p>
              </div>
              <input type="checkbox" checked={settings.showOrderDateTime} onChange={e => setSettings({
              ...settings,
              showOrderDateTime: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1085" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1086">
              <div data-id="element-1087">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1088">
                  Fecha/Hora Entrega
                </p>
              </div>
              <input type="checkbox" checked={settings.showDeliveryDateTime} onChange={e => setSettings({
              ...settings,
              showDeliveryDateTime: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1089" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1090">
              <div data-id="element-1091">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1092">
                  Tipo de Entrega
                </p>
              </div>
              <input type="checkbox" checked={settings.showDeliveryType} onChange={e => setSettings({
              ...settings,
              showDeliveryType: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1093" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1094">
              <div data-id="element-1095">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1096">
                  Código Empleado
                </p>
              </div>
              <input type="checkbox" checked={settings.showEmployeeCode} onChange={e => setSettings({
              ...settings,
              showEmployeeCode: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1097" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1098">
              <div data-id="element-1099">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1100">
                  Peticiones
                </p>
              </div>
              <input type="checkbox" checked={settings.showRequests} onChange={e => setSettings({
              ...settings,
              showRequests: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1101" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1102">
              <div data-id="element-1103">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1104">
                  Cantidad
                </p>
              </div>
              <input type="checkbox" checked={settings.showQuantity} onChange={e => setSettings({
              ...settings,
              showQuantity: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1105" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1106">
              <div data-id="element-1107">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1108">
                  Firma
                </p>
              </div>
              <input type="checkbox" checked={settings.showSignature} onChange={e => setSettings({
              ...settings,
              showSignature: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1109" />
            </label>
          </div>
        </Card>

        {/* Additional Sections */}
        <Card data-id="element-1110">
          <div className="flex items-center gap-3 mb-6" data-id="element-1111">
            <div className="p-2 bg-pink-100 dark:bg-pink-900/20 rounded-lg" data-id="element-1112">
              <FileText className="w-5 h-5 text-pink-600 dark:text-pink-400" data-id="element-1113" />
            </div>
            <div data-id="element-1114">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1115">
                Secciones Adicionales
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1116">
                Elementos extra del conduce
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-1117">
            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1118">
              <div data-id="element-1119">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-1120">
                  Sección de Notas
                </p>
              </div>
              <input type="checkbox" checked={settings.showNotes} onChange={e => setSettings({
              ...settings,
              showNotes: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1121" />
            </label>

            {settings.showNotes && <div className="ml-4" data-id="element-1122">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1123">
                  Etiqueta de Notas
                </label>
                <input type="text" value={settings.notesLabel} onChange={e => setSettings({
              ...settings,
              notesLabel: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Notas" data-id="element-1124" />
              </div>}

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-1125">
              <div data-id="element-1126">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-1127">
                  Pie de Página
                </p>
              </div>
              <input type="checkbox" checked={settings.showFooter} onChange={e => setSettings({
              ...settings,
              showFooter: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-1128" />
            </label>

            {settings.showFooter && <div className="ml-4" data-id="element-1129">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1130">
                  Texto del Pie de Página
                </label>
                <textarea value={settings.footerText} onChange={e => setSettings({
              ...settings,
              footerText: e.target.value
            })} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Documento generado por La Cochera Restaurant" data-id="element-1131" />
              </div>}
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3" data-id="element-1132">
          <Button variant="outline" onClick={() => {
          const defaultSettings = {
            companyName: 'La Cochera',
            companySubtitle: 'Restaurant',
            showLogo: true,
            showOrderDateTime: false,
            showDeliveryDateTime: false,
            showDeliveryType: false,
            showEmployeeCode: true,
            showRequests: false,
            showQuantity: true,
            showSignature: true,
            showNotes: true,
            notesLabel: 'Notas',
            showFooter: true,
            footerText: 'Documento generado por La Cochera Restaurant',
            primaryColor: '#000000',
            accentColor: '#3b82f6',
            fontFamily: 'Segoe UI',
            fontSize: '11pt',
            headerStyle: 'modern' as const,
            tableStyle: 'striped' as const,
            borderRadius: '8px',
            logoUrl: '',
            logoSize: '60px'
          };
          setSettings(defaultSettings);
          showToast('info', 'Valores restaurados', 'Se han restaurado los valores por defecto');
        }} data-id="element-1133">
            Restaurar Valores por Defecto
          </Button>
          <Button onClick={handleSave} leftIcon={<Save size={18} data-id="element-1135" />} size="lg" data-id="element-1134">
            Guardar Configuración
          </Button>
        </div>
      </div>
    </div>;
}